/**
 * ADR-0173 — pure auto-layout for the Users & Teams Organization tab.
 *
 * Solid edges form a forest (≤1 solid parent per person, structurally
 * guaranteed by the hub's partial unique index), so layout is a TIDY TREE
 * over the solid forest: each manager is centered over the span of their
 * reports, sibling subtrees never overlap, and tiers are stacked with
 * per-tier heights that account for tall boxes (stacks, contacts boxes).
 * Dotted edges render dashed, overlaid, and never affect layout.
 *
 * Box model (spec §0.7 — derived stacking with expand-to-edit):
 *   * user box — one named user.
 *   * stack box — the TRUE LEAVES under one solid manager collapse into one
 *     box at STACK_THRESHOLD or more. A true leaf's only relationship is the
 *     solid edge to its manager — no reports, no dotted edges either
 *     direction, no proxied contacts. Load-bearing: every rendered line runs
 *     box-to-box; no edge ever originates from a row inside a stack.
 *   * contacts box — all contacts proxied by one user share ONE dashed-outline
 *     box that hangs BELOW that user like a report (owner ruling 2026-08-22:
 *     contacts connect to their proxy like an employee to a manager; only the
 *     box SHAPE differs). It occupies a child slot in the tree. Contacts with
 *     no proxy share one unattached box in the strip BELOW the chart
 *     (rendering it at tier 0 read as "head of the org"; see spec §0.E).
 *
 * UNASSIGNED people (2026-08-22 owner defect report, the "test3/test4" case):
 * a user with NO relationships — no solid manager, no reports, no dotted
 * edges, no proxied contacts — is not a root; they render in an "Unassigned"
 * strip below the chart, so the top row holds only real org heads and adding
 * a person never reshuffles the tree. Drawing an edge lifts them in.
 *
 * Everything derived, nothing persisted; positions are always computed.
 * Deterministic: same data, same picture. Inactive members are flagged for
 * muted rendering — the chart shows TRUE structure, because edges and the
 * oversight closure survive deactivation.
 */

/**
 * Named presentation constant (spec §3.1): true leaves stack at 2+.
 * Originally 4 (§0.7's "10 operators under Mike" case); owner re-ruled to 2
 * on 2026-08-23 after prod use — two reports under one manager should fold.
 */
export const STACK_THRESHOLD = 2;

// Fixed spacing + estimated box heights — deterministic, no DOM measurement.
// Heights mirror the canvas CSS closely enough to reserve honest room; exact
// pixel truth is not required, only non-overlap.
export const BOX_WIDTH = 220;
export const COLUMN_GAP = 40;
export const TIER_GAP = 70;
export const USER_BOX_HEIGHT = 48;
export const STACK_HEADER_HEIGHT = 48;
export const STACK_ROW_HEIGHT = 28; // measured 2026-08-23: rows bearing a status pill render ~27px
export const CONTACTS_HEADER_HEIGHT = 36;
export const CONTACTS_ROW_HEIGHT = 22;

export interface OrgMemberInput {
  account_id: string;
  display_name: string | null;
  email: string;
  entitlement: string;
  status: string;
  account_type: "user" | "contact";
  proxy_account_id: string | null;
}

export interface OrgEdgeInput {
  id: string;
  account_id: string;
  manager_account_id: string;
  kind: "solid" | "dotted";
}

export interface OrgPersonCell {
  accountId: string;
  name: string;
  entitlement: string;
  inactive: boolean;
  /** The literal membership status — rendered on the box when not active. */
  status: string;
}

export type OrgBox =
  | ({ kind: "user"; id: string; x: number; y: number } & OrgPersonCell)
  | {
      kind: "stack";
      id: string;
      managerAccountId: string;
      members: OrgPersonCell[];
      x: number;
      y: number;
    }
  | {
      kind: "contacts";
      id: string;
      /** Null for the unattached (no-proxy) contacts box. */
      proxyAccountId: string | null;
      contacts: Array<{ accountId: string; name: string; inactive: boolean }>;
      x: number;
      y: number;
    };

export interface OrgLayoutEdge {
  /** The tenant_org_edges id for solid/dotted; synthetic for proxy connectors. */
  id: string;
  sourceBoxId: string;
  targetBoxId: string;
  kind: "solid" | "dotted" | "proxy";
}

export interface OrgLayoutResult {
  boxes: OrgBox[];
  edges: OrgLayoutEdge[];
  /** Non-interactive section labels (e.g. the Unassigned strip). */
  labels: Array<{ id: string; text: string; x: number; y: number }>;
}

export function memberName(m: { display_name: string | null; email: string }): string {
  const name = m.display_name?.trim();
  return name && name.length > 0 ? name : m.email;
}

export function stackBoxHeight(memberCount: number): number {
  return STACK_HEADER_HEIGHT + STACK_ROW_HEIGHT * memberCount;
}
export function contactsBoxHeight(contactCount: number): number {
  return CONTACTS_HEADER_HEIGHT + CONTACTS_ROW_HEIGHT * contactCount;
}

/** expandedStackIds sentinel for the Unassigned strip's stack. */
export const UNASSIGNED_STACK_KEY = "unassigned";

const userBoxId = (accountId: string) => `user:${accountId}`;
const stackBoxId = (managerId: string) => `stack:${managerId}`;
const contactsBoxId = (proxyId: string | null) => `contacts:${proxyId ?? "unattached"}`;

/** One slot in the tree: a user, a folded leaf stack, or a contacts box. */
interface TreeItem {
  boxId: string;
  sortKey: string;
  ownWidth: number;
  boxHeight: number;
  children: TreeItem[];
  tier: number;
  x: number;
  make: (x: number, y: number) => OrgBox;
}

/**
 * Compute the full chart. `expandedStackIds` holds manager account ids whose
 * leaf stack is expanded in place for edge-drawing (ephemeral UI state).
 */
export function computeOrgLayout(
  members: readonly OrgMemberInput[],
  edges: readonly OrgEdgeInput[],
  expandedStackIds: ReadonlySet<string> = new Set()
): OrgLayoutResult {
  const users = members.filter((m) => m.account_type === "user");
  const contacts = members.filter((m) => m.account_type === "contact");
  const userIds = new Set(users.map((u) => u.account_id));
  // Edges may reference accounts absent from the member list (races); keep
  // the picture honest by dropping edges with a missing endpoint.
  const liveEdges = edges.filter((e) => userIds.has(e.account_id) && userIds.has(e.manager_account_id));

  const solidManagerOf = new Map<string, OrgEdgeInput>();
  const dottedTouches = new Set<string>();
  const reportCount = new Map<string, number>();
  for (const e of liveEdges) {
    if (e.kind === "solid") {
      solidManagerOf.set(e.account_id, e);
      reportCount.set(e.manager_account_id, (reportCount.get(e.manager_account_id) ?? 0) + 1);
    } else {
      dottedTouches.add(e.account_id);
      dottedTouches.add(e.manager_account_id);
      reportCount.set(e.manager_account_id, (reportCount.get(e.manager_account_id) ?? 0) + 1);
    }
  }
  const proxiesOf = new Map<string, OrgMemberInput[]>();
  for (const c of contacts) {
    const key = c.proxy_account_id && userIds.has(c.proxy_account_id) ? c.proxy_account_id : "unattached";
    const list = proxiesOf.get(key);
    if (list) list.push(c);
    else proxiesOf.set(key, [c]);
  }

  // True leaf: sole relationship is the solid edge to a manager.
  function isTrueLeaf(accountId: string): boolean {
    return (
      solidManagerOf.has(accountId) &&
      (reportCount.get(accountId) ?? 0) === 0 &&
      !dottedTouches.has(accountId) &&
      !proxiesOf.has(accountId)
    );
  }

  const cell = (m: OrgMemberInput): OrgPersonCell => ({
    accountId: m.account_id,
    name: memberName(m),
    entitlement: m.entitlement,
    inactive: m.status !== "active",
    status: m.status
  });

  // Group stackable leaves by manager.
  const leavesByManager = new Map<string, OrgMemberInput[]>();
  for (const u of users) {
    if (!isTrueLeaf(u.account_id)) continue;
    const managerId = solidManagerOf.get(u.account_id)!.manager_account_id;
    const list = leavesByManager.get(managerId);
    if (list) list.push(u);
    else leavesByManager.set(managerId, [u]);
  }
  const stackedAccountIds = new Set<string>();
  const stackByManager = new Map<string, OrgMemberInput[]>();
  for (const [managerId, leaves] of leavesByManager) {
    if (leaves.length >= STACK_THRESHOLD && !expandedStackIds.has(managerId)) {
      const sorted = [...leaves].sort((a, b) => memberName(a).localeCompare(memberName(b)));
      stackByManager.set(managerId, sorted);
      for (const l of leaves) stackedAccountIds.add(l.account_id);
    }
  }

  // ── Build the solid forest as TreeItems ─────────────────────────────────
  // Unassigned = zero relationships of any kind; kept out of the tree.
  const isConnected = (u: OrgMemberInput): boolean =>
    solidManagerOf.has(u.account_id) ||
    (reportCount.get(u.account_id) ?? 0) > 0 ||
    dottedTouches.has(u.account_id) ||
    proxiesOf.has(u.account_id);
  const unassignedUsers = users
    .filter((u) => !stackedAccountIds.has(u.account_id) && !isConnected(u))
    .sort((a, b) => memberName(a).localeCompare(memberName(b)));
  const unassignedIds = new Set(unassignedUsers.map((u) => u.account_id));

  const itemByUser = new Map<string, TreeItem>();
  for (const u of users) {
    if (stackedAccountIds.has(u.account_id) || unassignedIds.has(u.account_id)) continue;
    itemByUser.set(u.account_id, {
      boxId: userBoxId(u.account_id),
      sortKey: memberName(u).toLocaleLowerCase(),
      ownWidth: BOX_WIDTH,
      boxHeight: USER_BOX_HEIGHT,
      children: [],
      tier: 0,
      x: 0,
      make: (x, y) => ({ kind: "user", id: userBoxId(u.account_id), x, y, ...cell(u) })
    });
  }
  for (const [managerId, stackMembers] of stackByManager) {
    const item: TreeItem = {
      boxId: stackBoxId(managerId),
      sortKey: memberName(stackMembers[0]!).toLocaleLowerCase(),
      ownWidth: BOX_WIDTH,
      boxHeight: stackBoxHeight(stackMembers.length),
      children: [],
      tier: 0,
      x: 0,
      make: (x, y) => ({
        kind: "stack",
        id: stackBoxId(managerId),
        managerAccountId: managerId,
        members: stackMembers.map(cell),
        x,
        y
      })
    };
    // The stack hangs under its manager like any other child slot.
    const managerItem = itemByUser.get(managerId);
    if (managerItem) managerItem.children.push(item);
  }
  // Contacts box: hangs below its proxy exactly like a report (owner ruling
  // 2026-08-22) — a child slot in the tree; only the box SHAPE is different.
  for (const [proxyId, proxied] of proxiesOf) {
    if (proxyId === "unattached") continue;
    const proxyItem = itemByUser.get(proxyId);
    if (!proxyItem) continue;
    proxyItem.children.push({
      boxId: contactsBoxId(proxyId),
      // \uffff sorts contacts after every named sibling — rightmost slot.
      sortKey: "\uffff",
      ownWidth: BOX_WIDTH,
      boxHeight: contactsBoxHeight(proxied.length),
      children: [],
      tier: 0,
      x: 0,
      make: (x, y) => ({
        kind: "contacts",
        id: contactsBoxId(proxyId),
        proxyAccountId: proxyId,
        contacts: [...proxied]
          .sort((a, b) => memberName(a).localeCompare(memberName(b)))
          .map((c) => ({ accountId: c.account_id, name: memberName(c), inactive: c.status !== "active" })),
        x,
        y
      })
    });
  }
  // Wire individual children under their managers; collect roots.
  const roots: TreeItem[] = [];
  for (const u of users) {
    if (stackedAccountIds.has(u.account_id) || unassignedIds.has(u.account_id)) continue;
    const item = itemByUser.get(u.account_id)!;
    const solid = solidManagerOf.get(u.account_id);
    const parent = solid ? itemByUser.get(solid.manager_account_id) : undefined;
    if (parent && parent !== item) parent.children.push(item);
    else if (!solid) roots.push(item);
  }
  // Cycle-defensive sweep: a (hub-refused, snapshot-only) solid cycle leaves
  // items unreachable from any root — surface them as extra roots so the walk
  // below terminates and everyone still renders.
  {
    const reachable = new Set<string>();
    const mark = (item: TreeItem) => {
      if (reachable.has(item.boxId)) return;
      reachable.add(item.boxId);
      for (const c of item.children) mark(c);
    };
    for (const r of roots) mark(r);
    for (const item of itemByUser.values()) {
      if (!reachable.has(item.boxId)) {
        // Break ONE cycle edge deterministically: treat this item as a root
        // (its parent pointer still renders as a solid edge below).
        roots.push(item);
        mark(item);
      }
    }
  }
  for (const item of itemByUser.values()) {
    item.children.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }
  roots.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  // Prune child links that revisit a placed item — with the sweep above this
  // breaks any defensive cycle exactly once, so measure/assign terminate.
  {
    const visited = new Set<string>();
    const prune = (item: TreeItem): void => {
      visited.add(item.boxId);
      item.children = item.children.filter((c) => !visited.has(c.boxId));
      for (const c of item.children) prune(c);
    };
    for (const r of roots) prune(r);
  }

  // ── Pass 1: tiers + subtree widths ──────────────────────────────────────
  const measured = new Map<string, number>(); // boxId -> subtree width
  function measure(item: TreeItem, tier: number, seen: Set<string>): number {
    // seen-guard is belt-and-braces with the cycle sweep above.
    if (seen.has(item.boxId)) return 0;
    seen.add(item.boxId);
    item.tier = tier;
    let childrenWidth = 0;
    for (const c of item.children) {
      const w = measure(c, tier + 1, seen);
      childrenWidth += (childrenWidth > 0 ? COLUMN_GAP : 0) + w;
    }
    const w = Math.max(item.ownWidth, childrenWidth);
    measured.set(item.boxId, w);
    return w;
  }
  const seen = new Set<string>();
  for (const r of roots) measure(r, 0, seen);

  // ── Pass 2: x assignment — children packed left-to-right, parent centered
  // over the span of its children (a childless node sits at its slot start).
  const placed: TreeItem[] = [];
  function assign(item: TreeItem, left: number): void {
    const width = measured.get(item.boxId) ?? item.ownWidth;
    if (item.children.length === 0) {
      item.x = left + (width - item.ownWidth) / 2;
    } else {
      let cursor = left;
      for (const c of item.children) {
        const cw = measured.get(c.boxId) ?? c.ownWidth;
        assign(c, cursor);
        cursor += cw + COLUMN_GAP;
      }
      const first = item.children[0]!;
      const last = item.children[item.children.length - 1]!;
      const childSpanCenter = (first.x + last.x + BOX_WIDTH) / 2;
      // Center over children, clamped into the subtree slot.
      item.x = Math.min(
        Math.max(childSpanCenter - BOX_WIDTH / 2, left),
        left + width - item.ownWidth
      );
    }
    placed.push(item);
  }
  {
    let cursor = 0;
    for (const r of roots) {
      const w = measured.get(r.boxId) ?? r.ownWidth;
      assign(r, cursor);
      cursor += w + COLUMN_GAP;
    }
  }

  // ── Pass 3: tier tops from per-tier content heights ─────────────────────
  const tierContentHeight = new Map<number, number>();
  for (const item of placed) {
    const h = item.boxHeight;
    tierContentHeight.set(item.tier, Math.max(tierContentHeight.get(item.tier) ?? 0, h));
  }
  const tierTop = new Map<number, number>();
  {
    const tiers = [...tierContentHeight.keys()].sort((a, b) => a - b);
    let y = 0;
    for (const t of tiers) {
      tierTop.set(t, y);
      y += (tierContentHeight.get(t) ?? USER_BOX_HEIGHT) + TIER_GAP;
    }
  }

  // ── Emit boxes ──────────────────────────────────────────────────────────
  const boxes: OrgBox[] = [];
  let chartBottom = 0;
  for (const item of placed) {
    const y = tierTop.get(item.tier) ?? 0;
    boxes.push(item.make(item.x, y));
    chartBottom = Math.max(chartBottom, y + item.boxHeight);
  }
  // The bottom strip: unassigned people + unattached contacts, clearly
  // outside the hierarchy. Adding someone here never moves the tree.
  const labels: OrgLayoutResult["labels"] = [];
  const unattached = proxiesOf.get("unattached");
  if (unassignedUsers.length > 0 || unattached) {
    const stripY = chartBottom + TIER_GAP + 28;
    labels.push({ id: "label:unassigned", text: "Unassigned", x: 0, y: stripY - 28 });
    let cursor = 0;
    // Same stacking rule as leaf reports (owner ruling 2026-08-22: "shouldn't
    // names be stacking?"): 4+ unassigned people fold into one stack box,
    // chevron-expanded (sentinel key) into individual boxes for edge-drawing.
    if (unassignedUsers.length >= STACK_THRESHOLD && !expandedStackIds.has(UNASSIGNED_STACK_KEY)) {
      boxes.push({
        kind: "stack",
        id: stackBoxId(UNASSIGNED_STACK_KEY),
        managerAccountId: UNASSIGNED_STACK_KEY,
        members: unassignedUsers.map(cell),
        x: cursor,
        y: stripY
      });
      cursor += BOX_WIDTH + COLUMN_GAP;
    } else {
      for (const u of unassignedUsers) {
        boxes.push({ kind: "user", id: userBoxId(u.account_id), x: cursor, y: stripY, ...cell(u) });
        cursor += BOX_WIDTH + COLUMN_GAP;
      }
    }
    if (unattached) {
      boxes.push({
        kind: "contacts",
        id: contactsBoxId(null),
        proxyAccountId: null,
        contacts: [...unattached]
          .sort((a, b) => memberName(a).localeCompare(memberName(b)))
          .map((c) => ({ accountId: c.account_id, name: memberName(c), inactive: c.status !== "active" })),
        x: cursor,
        y: stripY
      });
    }
  }

  // ── Edges — always box-to-box ───────────────────────────────────────────
  const layoutEdges: OrgLayoutEdge[] = [];
  for (const e of liveEdges) {
    if (stackedAccountIds.has(e.account_id)) continue; // folded into the stack edge below
    layoutEdges.push({
      id: e.id,
      sourceBoxId: userBoxId(e.manager_account_id),
      targetBoxId: userBoxId(e.account_id),
      kind: e.kind
    });
  }
  for (const managerId of stackByManager.keys()) {
    layoutEdges.push({
      id: `stackedge:${managerId}`,
      sourceBoxId: userBoxId(managerId),
      targetBoxId: stackBoxId(managerId),
      kind: "solid"
    });
  }
  for (const box of boxes) {
    if (box.kind === "contacts" && box.proxyAccountId) {
      layoutEdges.push({
        id: `proxyedge:${box.proxyAccountId}`,
        sourceBoxId: userBoxId(box.proxyAccountId),
        targetBoxId: box.id,
        kind: "proxy"
      });
    }
  }

  return { boxes, edges: layoutEdges, labels };
}
